import { IFeatureFlag, FeatureFlagItem } from '../interfaces/IFeatureFlag';
import apiService from "../../errorHandling/appService";

// Cache keys
const CACHE_KEY_VERSION = 'featureFlag_version';
const CACHE_KEY_FLAGS = 'featureFlag_flags';

export const getAllFeatureFlags = async (
  parentContext: any
): Promise<any> => {
  try {
    const auth = parentContext.authClient.authContext;
    const accounts = auth.getAllAccounts();
    const resource: any = { scopes: [process.env.REACT_APP_API_RESOURCE],};
    const apiEndPoint = `${process.env.REACT_APP_Consumption_API_ENDPOINT}`;
   const apiURL= apiEndPoint+'/api/FeatureFlag/feature-flags';
    if (accounts.length > 0) {
      auth.setActiveAccount(accounts[0]);
    }
    const token = await auth?.acquireTokenSilent(resource);
    const request = {
      headers: {
        Authorization: "Bearer " + token.accessToken,
      },
    };
     // Use the generic apiService for the request
     const response = await apiService(apiURL,'GET',null,request.headers)
    return response;
  } catch (err) {
    return err?.response || err;
  }
};

/**
 * Initialize feature flags from the API and refresh the browser cache.
 */
export const initializeFeatureFlags = async (
  parentContext: any
): Promise<any> => {
  try {
    const flagsResponse = await getAllFeatureFlags(parentContext);
    const responseData = flagsResponse?.data ?? flagsResponse;
    let flagsArray: FeatureFlagItem[] | null = null;

    if (Array.isArray(responseData)) {
      flagsArray = responseData;
    } else if (Array.isArray(responseData?.flags)) {
      flagsArray = responseData.flags;
    }

    if (!flagsArray) {
      throw new Error("Feature flag API returned an invalid response.");
    }

    const flags: IFeatureFlag = flagsArray.reduce((acc, item) => {
      if (item.featureName) {
        acc[item.featureName] = item.flag === true;
      }
      return acc;
    }, {} as IFeatureFlag);

    cacheFeatureFlags(flags);

    return {
      flags,
      fromCache: false
    };

  } catch (error) {
    console.error('Error initializing feature flags:', error);
    
    // Fallback to cached data if available
    const cachedFlags = getCachedFeatureFlags();
    
    if (cachedFlags && Object.keys(cachedFlags).length > 0) {
      console.warn('Using cached feature flags due to API error');
      return {
        flags: cachedFlags,
        fromCache: true,
        error: true
      };
    }
    
    throw error;
  }
};

/**
 * Get cached version from localStorage
 */
export const getCachedVersion = (): string | null => {
  return localStorage.getItem(CACHE_KEY_VERSION);
};

/**
 * Get cached feature flags from localStorage
 */
export const getCachedFeatureFlags = (): IFeatureFlag | null => {
  const cachedFlags = localStorage.getItem(CACHE_KEY_FLAGS);
  return cachedFlags ? JSON.parse(cachedFlags) : null;
};

/**
 * Save feature flags and version to cache
 */
export const cacheFeatureFlags = (flags: IFeatureFlag): void => {
  localStorage.setItem(CACHE_KEY_FLAGS, JSON.stringify(flags));
};

/**
 * Clear feature flag cache
 */
export const clearFeatureFlagCache = (): void => {
  localStorage.removeItem(CACHE_KEY_VERSION);
  localStorage.removeItem(CACHE_KEY_FLAGS);
};

/**
 * Check if a specific feature is enabled
 */
export const isFeatureEnabled = (featureName: string): boolean => {
  const cachedFlags = getCachedFeatureFlags();
  if (!cachedFlags) return false;

  return cachedFlags[featureName] || false;
};

// Generated by Copilot
// updated on: 2026-08-25 | 29 lines updated | [Auto Generated] | Prevented stale browser flags from overriding current API values | Time Taken 20 min